import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, unref as _unref, createVNode as _createVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "text-xl font-bold text-textPrimary text-left" }
const _hoisted_2 = {
  key: 0,
  class: "text-sm text-gray-600 mt-1 text-left"
}
const _hoisted_3 = { class: "flex items-center gap-4 w-full" }
const _hoisted_4 = {
  key: 1,
  class: "flex-1"
}

import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { storeToRefs } from 'pinia'

import ModalVue from '@/components/designSystem/modals/Modal.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import { useImportQualificationValidationsStore } from '@/pinia-store/useImportQualificationValidationsStore'
import type { ImportStep, ImportFormData, QualificationPolicyType } from './types'
import { DEFAULT_POLICY_TYPE } from './types'
import StepForm from './StepForm.vue'
import StepFile from './StepFile.vue'
import StepResult from './StepResult.vue'

const MODAL_CLOSE_ANIMATION_MS = 300


export default /*#__PURE__*/_defineComponent({
  __name: 'Index',
  props: {
    modelValue: { type: Boolean },
    policyType: { default: DEFAULT_POLICY_TYPE }
  },
  emits: ["update:modelValue", "imported"],
  setup(__props: any, { emit }) {

const props = __props;





const store = useImportQualificationValidationsStore()
const {
  isImporting,
  isValidatingSchedules,
  uploadProgress,
  uploadLoaded,
  uploadTotal,
  importResult
} = storeToRefs(store)

const currentStep = ref<ImportStep>('form')
const selectedFile = ref<File | null>(null)
const form = ref<ImportFormData>({
  company: null,
  healthUnit: null,
  schedules: []
})
const isFormValid = ref(false)

const policyLabel = computed(() =>
  props.policyType === 'DOCUMENTARY_QUALIFICATION'
    ? 'Qualificação Documental'
    : 'Qualificação para Repasse'
)

const headerTitle = computed(() => {
  switch (currentStep.value) {
    case 'form':
      return `Importar validações - ${policyLabel.value}`
    case 'file':
      return 'Selecione o arquivo'
    case 'result':
      return 'Importar validações'
    default:
      return ''
  }
})

const showLeftButton = computed(() => {
  if (currentStep.value === 'form') return true
  if (currentStep.value === 'file' && !isImporting.value) return true
  return false
})

const leftButtonText = computed(() => (currentStep.value === 'form' ? 'Cancelar' : 'Voltar'))

const rightButtonText = computed(() => {
  if (currentStep.value === 'form') return 'Selecionar arquivo'
  if (currentStep.value === 'file') return 'Salvar'
  if (currentStep.value === 'result') return 'Concluir'
  return ''
})

const rightButtonTestId = computed(() => {
  if (currentStep.value === 'form') return 'import-selecionar-button'
  if (currentStep.value === 'file') return 'import-importar-button'
  if (currentStep.value === 'result') return 'import-concluir-button'
  return ''
})

const isRightButtonDisabled = computed(() => {
  if (currentStep.value === 'form') return !isFormValid.value
  if (currentStep.value === 'file') return !selectedFile.value || isImporting.value
  return false
})

const handleBackAction = () => {
  if (currentStep.value === 'form') {
    handleClose()
  } else if (currentStep.value === 'file') {
    selectedFile.value = null
    currentStep.value = 'form'
  }
}

const validateAndProceedToFile = async () => {
  if (!form.value.company || !form.value.schedules.length) return
  const valid = await store.validateSchedules(
    form.value.company,
    form.value.schedules,
    props.policyType
  )
  if (valid && props.modelValue) currentStep.value = 'file'
}

const submitImport = async () => {
  if (!selectedFile.value || !form.value.company || !form.value.schedules.length) return

  const success = await store.importValidations(
    {
      companyId: form.value.company,
      scheduleIds: form.value.schedules,
      file: selectedFile.value
    },
    props.policyType
  )

  if (success && props.modelValue) currentStep.value = 'result'
}

const handleMainAction = async () => {
  if (currentStep.value === 'form') {
    await validateAndProceedToFile()
  } else if (currentStep.value === 'file') {
    await submitImport()
  } else if (currentStep.value === 'result') {
    handleClose()
  }
}

const onFileSelected = (file: File | null) => {
  selectedFile.value = file
}

let closeTimer: ReturnType<typeof setTimeout> | null = null

const resetModal = () => {
  if (closeTimer) {
    clearTimeout(closeTimer)
    closeTimer = null
  }
  currentStep.value = 'form'
  selectedFile.value = null
  form.value = { company: null, healthUnit: null, schedules: [] }
  isFormValid.value = false
  store.$reset()
}

const handleClose = () => {
  if (closeTimer) clearTimeout(closeTimer)
  const shouldEmitImported =
    currentStep.value === 'result' && (importResult.value?.imported ?? 0) > 0
  if (shouldEmitImported) emit('imported')
  emit('update:modelValue', false)
  closeTimer = setTimeout(() => {
    if (!props.modelValue) resetModal()
    closeTimer = null
  }, MODAL_CLOSE_ANIMATION_MS)
}

onBeforeUnmount(() => {
  resetModal()
})

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal) resetModal()
  }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ModalVue, {
    "modal-active": _ctx.modelValue,
    "has-header-border": "",
    "has-footer-border": "",
    onClose: handleClose,
    "max-width": "max-w-[800px]"
  }, {
    header: _withCtx(() => [
      _createElementVNode("h2", _hoisted_1, _toDisplayString(headerTitle.value), 1),
      (currentStep.value === 'form')
        ? (_openBlock(), _createElementBlock("p", _hoisted_2, " Selecione as empresas, unidades ou escalas que receberão as configurações importadas "))
        : _createCommentVNode("", true)
    ]),
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_3, [
        (showLeftButton.value)
          ? (_openBlock(), _createBlock(OutlinedButton, {
              key: 0,
              "data-testid": "import-voltar-button",
              text: leftButtonText.value,
              width: "100%",
              class: "!border-primary30 !text-primary30 !font-bold flex-1",
              onClick: handleBackAction
            }, null, 8, ["text"]))
          : (_openBlock(), _createElementBlock("div", _hoisted_4)),
        _createVNode(FilledButton, {
          "data-testid": rightButtonTestId.value,
          text: rightButtonText.value,
          width: "100%",
          loading: _unref(isImporting) || _unref(isValidatingSchedules),
          disabled: isRightButtonDisabled.value,
          class: "!bg-primary !text-white !font-bold hover:!bg-primary40 flex-1",
          onClick: handleMainAction
        }, null, 8, ["data-testid", "text", "loading", "disabled"])
      ])
    ]),
    default: _withCtx(() => [
      (currentStep.value === 'form')
        ? (_openBlock(), _createBlock(StepForm, {
            key: 0,
            modelValue: form.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form).value = $event)),
            valid: isFormValid.value,
            "onUpdate:valid": _cache[1] || (_cache[1] = ($event: any) => ((isFormValid).value = $event)),
            "policy-type": _ctx.policyType
          }, null, 8, ["modelValue", "valid", "policy-type"]))
        : (currentStep.value === 'file')
          ? (_openBlock(), _createBlock(StepFile, {
              key: 1,
              "model-value": selectedFile.value,
              uploading: _unref(isImporting),
              "upload-progress": _unref(uploadProgress),
              "upload-loaded": _unref(uploadLoaded),
              "upload-total": _unref(uploadTotal),
              "policy-type": _ctx.policyType,
              "onUpdate:modelValue": onFileSelected
            }, null, 8, ["model-value", "uploading", "upload-progress", "upload-loaded", "upload-total", "policy-type"]))
          : (_openBlock(), _createBlock(StepResult, {
              key: 2,
              imported: _unref(importResult)?.imported ?? 0,
              rejected: _unref(importResult)?.rejected ?? 0,
              "merged-duplicates": _unref(importResult)?.mergedDuplicates ?? 0,
              errors: _unref(importResult)?.errors ?? [],
              "errors-truncated": _unref(importResult)?.errorsTruncated ?? false,
              "policy-id": _unref(importResult)?.policyId ?? 0,
              "policy-name": policyLabel.value
            }, null, 8, ["imported", "rejected", "merged-duplicates", "errors", "errors-truncated", "policy-id", "policy-name"]))
    ]),
    _: 1
  }, 8, ["modal-active"]))
}
}

})